import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cpeHistoryService from '../services/cpeHistoryService';
import type { CreditImportResult, CreditImportStatus } from '../services/cpeHistoryService';

/**
 * State for external CPE credit imports on the CPE history page — one controller per
 * available source (PICPA/Aptify today, CPAx later). Render forms live in
 * `components/CreditImportPanel.tsx`.
 *
 * Nothing is imported without the user's explicit yes. The status call counts what each
 * source holds for them; if there is anything and they have never been asked about that
 * source, the one-time prompt appears. Yes imports; No records the answer. Either way the
 * question is never asked again for that source — the toolbar button is the way back in.
 *
 * mode:
 *  - 'prompt'   not yet asked and the source has activities for this user
 *  - 'imported' just said yes — shows the result and offers nightly sync
 *  - 'button'   everything else (asked already, or nothing to import right now)
 */

export type CreditImportMode = 'prompt' | 'imported' | 'button';

export interface CreditImportController {
  source: string;
  displayName: string;
  status: CreditImportStatus;
  mode: CreditImportMode;
  syncing: boolean;
  savingPreference: boolean;
  lastResult: CreditImportResult | null;
  error: string | null;
  /** Prompt: "Yes" — import now. */
  accept: () => Promise<void>;
  /** Prompt: "No" — record the answer, never ask again. */
  decline: () => Promise<void>;
  /** Imported follow-up: close it. */
  finish: () => void;
  /** Toolbar button: run the import again at any time. */
  syncNow: () => Promise<void>;
  setAutoSync: (enabled: boolean) => Promise<void>;
  clearError: () => void;
}

type Phase = 'idle' | 'imported' | 'done';
type Busy = 'sync' | 'preference';

export function useCreditImports(
  onImported?: (result: CreditImportResult, status: CreditImportStatus) => void,
): { imports: CreditImportController[]; loaded: boolean } {
  const [statuses, setStatuses] = useState<CreditImportStatus[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [phases, setPhases] = useState<Record<string, Phase>>({});
  const [busy, setBusy] = useState<Record<string, Busy | undefined>>({});
  const [results, setResults] = useState<Record<string, CreditImportResult>>({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  // Latest callback/statuses without re-creating the actions on every parent render.
  const onImportedRef = useRef(onImported);
  useEffect(() => {
    onImportedRef.current = onImported;
  }, [onImported]);
  const statusesRef = useRef(statuses);
  useEffect(() => {
    statusesRef.current = statuses;
  }, [statuses]);

  useEffect(() => {
    let cancelled = false;
    cpeHistoryService
      .getCreditImports()
      .then((list) => {
        if (!cancelled) setStatuses(list.filter((s) => s.available));
      })
      .catch(() => {
        // Imports are a convenience; without status they simply stay hidden.
        if (!cancelled) setStatuses([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const patchStatus = useCallback((source: string, patch: Partial<CreditImportStatus>) => {
    setStatuses((prev) => prev.map((s) => (s.source === source ? { ...s, ...patch } : s)));
  }, []);

  const setError = useCallback((source: string, message: string | undefined) => {
    setErrors((prev) => ({ ...prev, [source]: message }));
  }, []);

  /** Runs the import for one source; returns true on success. Any import also answers the prompt. */
  const runImport = useCallback(
    async (source: string): Promise<boolean> => {
      setBusy((prev) => ({ ...prev, [source]: 'sync' }));
      setError(source, undefined);
      try {
        const result = await cpeHistoryService.runCreditImport(source);
        setResults((prev) => ({ ...prev, [source]: result }));
        const current = statusesRef.current.find((s) => s.source === source);
        const next = current
          ? { ...current, lastSyncAt: result.syncedAt, promptedAt: current.promptedAt ?? result.syncedAt }
          : null;
        if (next) {
          patchStatus(source, next);
          onImportedRef.current?.(result, next);
        }
        return true;
      } catch {
        const name = statusesRef.current.find((s) => s.source === source)?.displayName ?? 'Credit';
        setError(source, `${name} import failed. Try again later.`);
        return false;
      } finally {
        setBusy((prev) => ({ ...prev, [source]: undefined }));
      }
    },
    [patchStatus, setError],
  );

  const setAutoSync = useCallback(
    async (source: string, enabled: boolean) => {
      setBusy((prev) => ({ ...prev, [source]: 'preference' }));
      patchStatus(source, { autoSyncEnabled: enabled });
      try {
        await cpeHistoryService.updateCreditImportPreferences(source, { autoSyncEnabled: enabled });
      } catch {
        patchStatus(source, { autoSyncEnabled: !enabled });
        setError(source, 'Could not save your sync preference. Try again.');
      } finally {
        setBusy((prev) => ({ ...prev, [source]: undefined }));
      }
    },
    [patchStatus, setError],
  );

  const decline = useCallback(
    async (source: string) => {
      const current = statusesRef.current.find((s) => s.source === source);
      patchStatus(source, { promptedAt: current?.promptedAt ?? new Date().toISOString() });
      try {
        await cpeHistoryService.updateCreditImportPreferences(source, { markPrompted: true });
      } catch {
        // Collapsed locally already; the server will ask again on a later visit.
      }
    },
    [patchStatus],
  );

  const imports = useMemo<CreditImportController[]>(
    () =>
      statuses.map((status) => {
        const { source } = status;
        const phase = phases[source] ?? 'idle';
        const mode: CreditImportMode =
          phase === 'imported'
            ? 'imported'
            : phase === 'idle' && !status.promptedAt && (status.pendingCreditCount ?? 0) > 0
              ? 'prompt'
              : 'button';
        return {
          source,
          displayName: status.displayName,
          status,
          mode,
          syncing: busy[source] === 'sync',
          savingPreference: busy[source] === 'preference',
          lastResult: results[source] ?? null,
          error: errors[source] ?? null,
          accept: async () => {
            if (await runImport(source)) setPhases((prev) => ({ ...prev, [source]: 'imported' }));
          },
          decline: () => decline(source),
          finish: () => setPhases((prev) => ({ ...prev, [source]: 'done' })),
          syncNow: async () => {
            await runImport(source);
          },
          setAutoSync: (enabled: boolean) => setAutoSync(source, enabled),
          clearError: () => setError(source, undefined),
        };
      }),
    [statuses, phases, busy, results, errors, runImport, decline, setAutoSync, setError],
  );

  return { imports, loaded };
}
